import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Icon } from "@/lib/icons";
import { PaymentMethod } from "@/lib/types/payments";
import { ContributePageHeading } from "@/components/contribute/ContributePageHeading";
import {
  PayoutRequestHistory,
  GetPaidNotice, GetPaidField, GetPaidDialogContent,
  payoutContainerClass, payoutPageShellClass,
  payoutPrimaryActionClass, payoutSecondaryActionClass,
  payoutSelectTriggerClass, payoutFieldControlClass,
  payoutOutlineBadgeClass,
} from "@/components/get-paid";
import {
  PHONEME_RECORDING_LIMIT, PHRASE_RECORDING_LIMIT, TRANSLATION_TIME_LIMIT } from "./constants";
import { formatCurrency, formatNumber, formatDuration } from "./helpers";
import { useWorkspaceCopy } from "../contributor-workspace/use-workspace-copy";
import { ContributorWorkspaceShell } from "../contributor-workspace/ContributorWorkspaceShell";
import type { GetPaidCollectorTab } from "./GetPaidCollectorPanelNav";
import { QualificationTestWorkspace } from "./QualificationTestWorkspace";
import { EarningsOverview } from "./EarningsOverview";
import { earningsCopy } from "./earnings-copy";
import type { useGetPaid } from "./useGetPaid";

export function ContributorView({ ctx }: { ctx: ReturnType<typeof useGetPaid> }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [section, setSection] = useState<GetPaidCollectorTab>(() => {
    const tab = searchParams.get("section") as GetPaidCollectorTab | null;
    return tab ?? "overview";
  });

  useEffect(() => {
    const tab = searchParams.get("section") as GetPaidCollectorTab | null;
    if (tab && tab !== section) {
      setSection(tab);
    }
  }, [section, searchParams]);

  const handleSectionChange = (value: GetPaidCollectorTab) => {
    setSection(value);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("section", value);
      return next;
    }, { replace: true });
  };

  const {
    user, t, tPage, locale, currentLang, language, navigate,
    payoutState, currentTest, myPayoutRequests,
    availableLanguages, selectedTestLang, setSelectedTestLang,
    latestPendingUserContract, latestSignedUserContract,
    loadingLanguages,
    dataPhonemes, dataPhrasesLocalFr, dataPhrasesLocalOnly,
    testTimeRemaining, isTestExpired, submittingUserTest,
    statuses, attempts, timers, audioUrls, translations, activeItemId,
    startingTest, isStartTestBlocked, hasPendingPayoutRequest,
    awaitingPayoutAmount, formattedEarnings, selectedSubmission,
    getRequestPhone, loadingRequests,
    payoutDialogOpen, setPayoutDialogOpen,
    profileForm, setProfileForm, submittingProfileForm,
    bulkApproveDialog, setBulkApproveDialog, executeBulkApprove,
    rejectDialog, setRejectDialog, handleRejectWithDialog,
    handleStartTest, handleFinalSubmit, handleProfileSubmit, handleRequestPayout,
    handleOpenContracts,
    loadUserContracts,
    startRecording, stopRecording, redoRecording,
    handleTranslationFocus, handleTranslationChange, allItemsCompleted,
  } = ctx;

  const currency = "XOF";
  const workspaceCopy = useWorkspaceCopy();

  if (section === "test") {
    return (
      <ContributorWorkspaceShell copy={workspaceCopy}>
        <QualificationTestWorkspace ctx={ctx} embedded />
      </ContributorWorkspaceShell>
    );
  }

  if (currentTest) {
    return (
      <div className={payoutPageShellClass}>
        <div className={payoutContainerClass}>
          <div className="sticky top-16 z-40 bg-background/90 backdrop-blur border-b py-3">
            <div className="flex items-center justify-between">
              <div>
                <Badge variant="outline" className={payoutOutlineBadgeClass}>{t.getPaid.qualificationTest}</Badge>
                <h1 className="text-xl font-semibold text-foreground mt-1">{currentTest.title}</h1>
                <p className={isTestExpired ? "mt-1 text-sm font-medium text-danger" : "mt-1 text-sm text-muted-foreground"}>
                  {t.getPaid.testTimeRemaining}: {formatDuration(testTimeRemaining)}
                </p>
              </div>
              <Button onClick={handleFinalSubmit} disabled={!allItemsCompleted() || submittingUserTest || isTestExpired} className="gap-2">
                {submittingUserTest ? (
                  <><Icon name="loader2" className="h-4 w-4 animate-spin" />{t.getPaid.submitting}</>
                ) : (
                  <><Icon name="check" className="h-4 w-4" />{t.getPaid.submitTest}</>
                )}
              </Button>
            </div>
          </div>

          <div className="space-y-8">
            <section className="space-y-3">
              <h2 className="text-lg font-semibold">{t.getPaid.sectionPhonemes}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {dataPhonemes.map((p: any, i: number) => {
                  const id = `ph-${i}`;
                  const status = statuses[id] ?? "idle";
                  const att = attempts[id] ?? 0;
                  const remaining = timers[id] ?? PHONEME_RECORDING_LIMIT;
                  const hasAudio = !!audioUrls[id];
                  const inProgress = activeItemId === id && status === "in_progress";
                  return (
                    <div key={id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-3xl font-bold text-primary">{p}</span>
                        <span className={status === "completed" ? "text-success" : status === "in_progress" ? "text-warning" : "text-danger"}>
                          {status === "completed" ? "✓" : status === "in_progress" ? "⏱️" : "✗"}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">{t.getPaid.attempt} {Math.min(att + 1, 2)}/2</div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" className="gap-1 h-8 px-2 text-xs" onClick={() => startRecording(id, PHONEME_RECORDING_LIMIT)} disabled={att >= 2 || inProgress || isTestExpired}>
                          <Icon name="mic" className="h-4 w-4" />
                          {inProgress ? `${t.getPaid.recording}... ${remaining || 0}s` : `${t.record} (${PHONEME_RECORDING_LIMIT}s)`}
                        </Button>
                        {inProgress && (
                          <Button size="sm" variant="outline" onClick={stopRecording}>{t.getPaid.stop}</Button>
                        )}
                      </div>
                      {hasAudio && (
                        <div className="flex items-center gap-2">
                          <audio controls src={audioUrls[id] || undefined} className="w-full" />
                          <Button size="sm" variant="secondary" onClick={() => redoRecording(id, PHONEME_RECORDING_LIMIT)} disabled={(attempts[id] ?? 0) >= 2 || isTestExpired}>{t.getPaid.redo}</Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-lg font-semibold">{t.getPaid.sectionPhrases}</h2>
              <div className="grid grid-cols-1 gap-4">
                {dataPhrasesLocalFr.map((ph: any, i: number) => {
                  const id = `pp-${i}`;
                  const status = statuses[id] ?? "idle";
                  const att = attempts[id] ?? 0;
                  const remaining = timers[id] ?? PHRASE_RECORDING_LIMIT;
                  const hasAudio = !!audioUrls[id];
                  const inProgress = activeItemId === id && status === "in_progress";
                  return (
                    <div key={id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{ph.local}</p>
                          <p className="text-sm text-muted-foreground">{ph.french}</p>
                        </div>
                        <span className={status === "completed" ? "text-success" : status === "in_progress" ? "text-warning" : "text-danger"}>
                          {status === "completed" ? "✓" : status === "in_progress" ? "⏱️" : "✗"}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">{t.getPaid.attempt} {Math.min(att + 1, 2)}/2</div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" className="gap-1 h-8 px-2 text-xs" onClick={() => startRecording(id, PHRASE_RECORDING_LIMIT)} disabled={att >= 2 || inProgress || isTestExpired}>
                          <Icon name="mic" className="h-4 w-4" />
                          {inProgress ? `${t.getPaid.recording}... ${remaining || 0}s` : `${t.record} (${PHRASE_RECORDING_LIMIT}s)`}
                        </Button>
                        {inProgress && (
                          <Button size="sm" variant="outline" onClick={stopRecording}>{t.getPaid.stop}</Button>
                        )}
                      </div>
                      {hasAudio && (
                        <div className="flex items-center gap-2">
                          <audio controls src={audioUrls[id] || undefined} className="w-full" />
                          <Button size="sm" variant="secondary" onClick={() => redoRecording(id, PHRASE_RECORDING_LIMIT)} disabled={(attempts[id] ?? 0) >= 2 || isTestExpired}>{t.getPaid.redo}</Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-lg font-semibold">{t.getPaid.sectionTranslations}</h2>
              <div className="grid grid-cols-1 gap-4">
                {dataPhrasesLocalOnly.map((ph: any, i: number) => {
                  const id = `tp-${i}`;
                  const status = statuses[id] ?? "idle";
                  const remaining = timers[id] ?? TRANSLATION_TIME_LIMIT;
                  const val = translations[id] || "";
                  return (
                    <div key={id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{ph.local}</p>
                        <span className={status === "completed" ? "text-success" : status === "in_progress" ? "text-warning" : "text-danger"}>
                          {status === "completed" ? "✓" : status === "in_progress" ? "⏱️" : "✗"}
                        </span>
                      </div>
                      <textarea
                        className="w-full min-h-24 rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder={t.getPaid.translationPlaceholder}
                        value={val}
                        disabled={isTestExpired}
                        onFocus={() => handleTranslationFocus(id)}
                        onChange={(e) => handleTranslationChange(id, e.target.value)}
                      />
                      <div className="text-xs text-muted-foreground">{t.getPaid.time}: {timers[id] !== undefined ? (remaining || 0) : TRANSLATION_TIME_LIMIT}s</div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          <div className="sticky bottom-0 z-40 bg-background/90 backdrop-blur border-t py-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{t.getPaid.submitEnabledWhenComplete}</p>
              <Button onClick={handleFinalSubmit} disabled={!allItemsCompleted() || submittingUserTest || isTestExpired} className="gap-2">
                {submittingUserTest ? (
                  <><Icon name="loader2" className="h-4 w-4 animate-spin" />{t.getPaid.submitting}</>
                ) : (
                  <><Icon name="check" className="h-4 w-4" />{t.getPaid.submitTest}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ContributorWorkspaceShell copy={workspaceCopy}>
      <Tabs value={section} onValueChange={(value) => handleSectionChange(value as GetPaidCollectorTab)} className="space-y-6">
        <div className="min-w-0 space-y-6">
              <TabsContent value="overview" className="mt-0">
                <EarningsOverview
                  copy={earningsCopy[currentLang === "en" ? "en" : "fr"]}
                  lang={currentLang}
                  validated={payoutState.validatedAudios}
                  pending={payoutState.pendingAudios}
                  progress={payoutState.progress}
                  remaining={payoutState.step1Remaining}
                  thresholdReached={payoutState.step1Reached}
                  testSubmitted={Boolean(selectedSubmission)}
                  testApproved={payoutState.testApproved}
                  availableAmount={payoutState.earnedAmount}
                  requestedAmount={awaitingPayoutAmount}
                  hasPendingRequest={hasPendingPayoutRequest}
                  formatAmount={(amount) => formatCurrency(amount, locale, currency)}
                  formatNumber={(value) => formatNumber(value, locale)}
                  onRequestPayout={handleRequestPayout}
                />
              </TabsContent>

              <TabsContent value="test" className="mt-0 space-y-6">
                <ContributePageHeading
                  eyebrow={t.getPaid.qualificationTest}
                  title={tPage.testTitle || "Validation Test"}
                  description={tPage.testSubtitle || "Complete the validation test to unlock earnings"}
                />

                {payoutState.step1Reached && !payoutState.testApproved && (
                  <GetPaidNotice tone="emerald" className="w-full">
                    <div className="flex items-start gap-3">
                      <span className="text-base">✨</span>
                      <div>
                        <p className="font-semibold">{tPage.eligibleForTest}</p>
                        <p className="mt-1 text-sm opacity-85">{tPage.canNowTakeTest}</p>
                      </div>
                    </div>
                  </GetPaidNotice>
                )}

                {payoutState.step2Passed && (
                  <GetPaidNotice tone="blue" className="w-full">
                    {tPage.newValidatedAfterTest.replace("{language}", selectedTestLang).replace("{count}", formatNumber(payoutState.validatedAudiosAfterTest, locale))}
                  </GetPaidNotice>
                )}

                <div className="py-4">
                  <GetPaidField label={tPage.testLanguageLabel} htmlFor="test-lang" className="max-w-sm">
                    <Select value={selectedTestLang} onValueChange={(v) => setSelectedTestLang(v)}>
                      <SelectTrigger className={payoutSelectTriggerClass} id="test-lang">
                        <SelectValue placeholder={tPage.chooseLanguage} />
                      </SelectTrigger>
                      <SelectContent>
                        {loadingLanguages ? (
                          <div className="p-2 text-sm text-muted-foreground">{tPage.loadingLanguages}</div>
                        ) : availableLanguages.length > 0 ? (
                          availableLanguages.map((lang: any) => (
                            <SelectItem key={lang.id || lang.code} value={lang.code}>
                              {lang.flag && <span>{lang.flag} </span>}
                              {lang.displayName || lang.name}
                            </SelectItem>
                          ))
                        ) : (
                          <div className="p-2 text-sm text-muted-foreground">{tPage.noLanguagesAvailable}</div>
                        )}
                      </SelectContent>
                    </Select>
                  </GetPaidField>

                  <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    {selectedSubmission ? (
                      <p className="text-xs text-muted-foreground/72">
                        {tPage.statusLabel} <Badge variant="secondary" className="rounded-full">{selectedSubmission.status === 'PENDING' ? tPage.testStatusPending : selectedSubmission.status === 'VALIDATED' ? tPage.testStatusValidated : tPage.testStatusRejected}</Badge>
                        {selectedSubmission.submittedAt ? <span className="ml-2">{new Date(selectedSubmission.submittedAt).toLocaleString(locale)}</span> : null}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground/72">{tPage.noTestSubmitted}</p>
                    )}
                    <Button onClick={handleStartTest} disabled={isStartTestBlocked} className={payoutPrimaryActionClass}>
                      <Icon name="bookOpen" className="mr-2 h-4 w-4" />
                      {startingTest ? tPage.loading : tPage.startTest}
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="requests" className="mt-0">
                <PayoutRequestHistory
                  title={tPage.myPaymentRequests}
                  requests={myPayoutRequests}
                  emptyTitle={tPage.noPaymentRequests}
                  emptyHint={payoutState.eligible ? tPage.eligibleCreateRequest : undefined}
                  labels={{
                    PENDING: tPage.paymentStatusPending, APPROVED: tPage.paymentStatusApproved,
                    REJECTED: tPage.paymentStatusRejected, PAID: tPage.paid,
                    requestedOn: tPage.requestedOn, moderatorNote: t.getPaid.moderatorNoteLabel,
                    refundedTitle: t.getPaid.refundedAmountTitle,
                    refundedDescription: (amount: string) => t.getPaid.refundedAmountDesc.replace("{amount}", amount),
                    transactionReference: (reference: string) => t.getPaid.transactionRefLabel.replace("{ref}", reference),
                    paidOn: (date: string) => t.getPaid.paidOnLabel.replace("{date}", date),
                    pendingInfo: t.getPaid.requestPendingModerator, approvedInfo: t.getPaid.requestApprovedAdminSoon,
                  }}
                  formatAmount={(amount: number) => formatCurrency(amount, locale, currency)}
                  formatDate={(date: string) => new Date(date).toLocaleString(locale)}
                  getPhone={getRequestPhone}
                  onCreateRequest={ctx.handleRequestPayout}
                  isEligible={payoutState.eligible}
                  createRequestLabel={`+ ${tPage.createPaymentRequest || t.getPaid.createRequest || "Créer demande"}`}
                />
              </TabsContent>

              <TabsContent value="contracts" className="mt-0 space-y-6">
                <ContributePageHeading
                  eyebrow={t.getPaid.contractsLabel || "Contracts"}
                  title={tPage.contractsTitle || "My Contracts"}
                  description={tPage.contractsDescription || "Manage your validation contracts"}
                />

                {latestPendingUserContract && (
                  <GetPaidNotice tone="amber" className="w-full">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="flex items-start gap-3">
                        <Icon name="alertTriangle" className="mt-0.5 h-4 w-4 shrink-0" />
                        <div className="space-y-1">
                          <p className="font-semibold">{t.getPaid.contractSentSignRequired}</p>
                          <p>{tPage.contractSignatureBlocked}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 md:shrink-0">
                        <Button variant="outline" onClick={loadUserContracts} className={payoutSecondaryActionClass}>
                          <Icon name="refreshCw" className="mr-2 h-4 w-4" />{tPage.refreshContracts}
                        </Button>
                        <Button onClick={() => handleOpenContracts('pending')} className={payoutPrimaryActionClass}>
                          <Icon name="fileText" className="mr-2 h-4 w-4" />{tPage.signContract}
                        </Button>
                      </div>
                    </div>
                  </GetPaidNotice>
                )}

                {!latestPendingUserContract && latestSignedUserContract && (
                  <GetPaidNotice tone="blue" className="w-full">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <span>{t.getPaid.signedContractAvailable}</span>
                      <Button variant="outline" onClick={() => handleOpenContracts('signed')} className={payoutSecondaryActionClass}>
                        <Icon name="eye" className="mr-2 h-4 w-4" />{tPage.viewSignedContract}
                      </Button>
                    </div>
                  </GetPaidNotice>
                )}

                {!latestPendingUserContract && !latestSignedUserContract && (
                  <div className="rounded-lg border border-dashed border-border/40 py-8 text-center text-sm text-muted-foreground/72 px-4">
                    {t.getPaid.noContracts || "No contracts available"}
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => handleOpenContracts('pending')} className={payoutSecondaryActionClass}>
                    <Icon name="fileText" className="mr-2 h-4 w-4" />{tPage.myContracts}
                  </Button>
                  <Button variant="outline" onClick={loadUserContracts} className={payoutSecondaryActionClass}>
                    <Icon name="refreshCw" className="mr-2 h-4 w-4" />{tPage.refreshContracts}
                  </Button>
                </div>
              </TabsContent>
        </div>
      </Tabs>

        <Dialog open={payoutDialogOpen} onOpenChange={setPayoutDialogOpen}>
          <GetPaidDialogContent className="max-w-lg">
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <DialogHeader>
                <DialogTitle>{tPage.profileDialog.title}</DialogTitle>
                <DialogDescription>{tPage.profileDialog.description}</DialogDescription>
              </DialogHeader>
              <div className="rounded-md border border-primary/20 bg-primary/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">{tPage.profileDialog.amountLabel}</p>
                <p className="text-2xl font-semibold text-primary">{formattedEarnings}</p>
                <p className="mt-1 text-xs text-muted-foreground">{tPage.profileDialog.missingInfo}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">{t.getPaid.withdrawAmountLabel}</Label>
                <Input className={payoutFieldControlClass} id="amount" type="number" min="1" value={profileForm.amount}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, amount: event.target.value }))}
                  placeholder={tPage.amountPlaceholder} required />
                <p className="text-xs text-muted-foreground">{tPage.availableAmount.replace("{amount}", formattedEarnings)}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{tPage.profileDialog.phoneLabel}</Label>
                <Input className={payoutFieldControlClass} id="phone" type="tel" value={profileForm.phone}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, phone: event.target.value }))}
                  placeholder={tPage.profileDialog.phonePlaceholder} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="provider">{tPage.profileDialog.providerLabel}</Label>
                <Select value={profileForm.provider} onValueChange={(value) => setProfileForm((prev) => ({ ...prev, provider: value }))}>
                  <SelectTrigger className={payoutSelectTriggerClass} id="provider">
                    <SelectValue placeholder={tPage.profileDialog.providerPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={PaymentMethod.FLOOZ}>FLOOZ</SelectItem>
                    <SelectItem value={PaymentMethod.TMONEY}>T-Money</SelectItem>
                    <SelectItem value={PaymentMethod.WAVE}>Wave</SelectItem>
                    <SelectItem value={PaymentMethod.BANK_TRANSFER}>{language === "fr" ? "Transfert bancaire" : "Bank transfer"}</SelectItem>
                    <SelectItem value={PaymentMethod.CASH}>{language === "fr" ? "Espèces" : "Cash"}</SelectItem>
                    <SelectItem value={PaymentMethod.OTHER}>{language === "fr" ? "Autre" : "Other"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="accountName">{tPage.profileDialog.accountNameLabel}</Label>
                <Input className={payoutFieldControlClass} id="accountName" value={profileForm.accountName}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, accountName: event.target.value }))}
                  placeholder={tPage.profileDialog.accountNamePlaceholder} required />
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="ghost" onClick={() => setPayoutDialogOpen(false)} disabled={submittingProfileForm}>{tPage.profileDialog.cancel}</Button>
                <Button type="submit" disabled={submittingProfileForm}>
                  {submittingProfileForm ? (
                    <>
                      <Icon name="loader2" className="mr-2 h-4 w-4 animate-spin" />
                      {tPage.profileDialog.submitting || t.getPaid.processing || "Envoi..."}
                    </>
                  ) : (
                    <>
                      <Icon name="wallet" className="mr-2 h-4 w-4" />
                      {tPage.profileDialog.submit}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </GetPaidDialogContent>
        </Dialog>

        <Dialog open={bulkApproveDialog.open} onOpenChange={(open) => setBulkApproveDialog((prev) => ({ ...prev, open }))}>
          <GetPaidDialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t.getPaid.bulkApproveConfirmTitle}</DialogTitle>
              <DialogDescription>
                {bulkApproveDialog.target === "moderator" ? t.getPaid.moderators : t.getPaid.contributors} : {bulkApproveDialog.count} {t.getPaid.requestsLower}
                pour {formatCurrency(bulkApproveDialog.totalAmount, locale, currency)}.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBulkApproveDialog({ open: false, target: null, count: 0, totalAmount: 0 })}>
                {t.getPaid.cancel}
              </Button>
              <Button type="button" disabled={!bulkApproveDialog.target || loadingRequests}
                onClick={() => bulkApproveDialog.target && executeBulkApprove(bulkApproveDialog.target)}>
                {loadingRequests ? t.getPaid.processing : t.getPaid.approve}
              </Button>
            </DialogFooter>
          </GetPaidDialogContent>
        </Dialog>

        <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog((prev) => ({ ...prev, open }))}>
          <GetPaidDialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t.getPaid.rejectRequestTitle}</DialogTitle>
              <DialogDescription>{t.getPaid.rejectDescription}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="reject-note">{t.getPaid.noteLabel}</Label>
              <Input className={payoutFieldControlClass} id="reject-note" value={rejectDialog.note}
                onChange={(event) => setRejectDialog((prev) => ({ ...prev, note: event.target.value }))}
                placeholder={t.getPaid.rejectNotePlaceholder} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRejectDialog({ open: false, requestId: null, note: "" })}>
                {t.getPaid.cancel}
              </Button>
              <Button type="button" variant="destructive" onClick={handleRejectWithDialog}>
                {t.getPaid.reject}
              </Button>
            </DialogFooter>
          </GetPaidDialogContent>
        </Dialog>
    </ContributorWorkspaceShell>
  );
}
